import { createSlice } from '@reduxjs/toolkit';

import type { EnvioFisicoArDocumentoTipo, EnvioFisicoArDto } from '../../types/envioFisicoArTypes';
import { abrirDocumentoEnvioFisicoAr, fetchEnviosFisicosAr } from './actions';

interface EnvioFisicoArState {
  processoArbitralId: string | null;
  itens: EnvioFisicoArDto[];
  total: number;
  pagina: number;
  hasMore: boolean;
  /** Primeira página em voo. */
  carregando: boolean;
  carregandoMais: boolean;
  /**
   * A primeira leitura já respondeu. É o que decide se a aba existe: antes disso "zero
   * cartas" e "ainda não sei" são iguais, e esconder a aba no primeiro caso e mostrá-la
   * depois faria ela piscar para quem tem envio.
   */
  carregado: boolean;
  erro: string | null;
  /**
   * A página cuja leitura falhou, para o "Tentar novamente" refazer ESSA página. Reler a 1
   * depois de uma falha no "Carregar mais" descartava as páginas que já estavam na tela.
   */
  paginaQueFalhou: number | null;
  /** `requestId` da leitura mais recente — resposta atrasada de outro processo é descartada. */
  requestId: string | null;
  /**
   * `envioFisicoId:tipo` → `requestId` de cada documento sendo aberto. Por DOCUMENTO, como o
   * `acaoEmVoo` da autorização: uma trava única deixava os outros "Ver" com cara de
   * habilitados e ignorava o clique neles em silêncio.
   */
  documentosEmVoo: Record<string, string>;
}

const initialState: EnvioFisicoArState = {
  processoArbitralId: null,
  itens: [],
  total: 0,
  pagina: 0,
  hasMore: false,
  carregando: false,
  carregandoMais: false,
  carregado: false,
  erro: null,
  paginaQueFalhou: null,
  requestId: null,
  documentosEmVoo: {},
};

/** @example chaveDoDocumento(envio.id, 'CARTA_ENVIADA') // 'uuid:CARTA_ENVIADA' */
export const chaveDoDocumento = (envioFisicoId: string, tipo: EnvioFisicoArDocumentoTipo) =>
  `${envioFisicoId}:${tipo}`;

/**
 * Respostas que querem dizer "não há aba para você", e não falha: 404 é o que o servidor dá
 * a quem está do lado do requerido (e a processo de outro escritório); 403, a quem não tem
 * acesso ao domínio arbitral. Mostrar erro nesses casos exibiria a aba justamente a quem ela
 * é negada.
 */
const STATUS_SEM_ABA: ReadonlySet<number | null> = new Set([403, 404]);

function destravar(state: EnvioFisicoArState, chave: string, requestId: string) {
  // Só quem trancou destranca: um clique novo no mesmo documento já trocou o `requestId`.
  if (state.documentosEmVoo[chave] === requestId) delete state.documentosEmVoo[chave];
}

const envioFisicoArSlice = createSlice({
  name: 'envioFisicoAr',
  initialState,
  reducers: {
    limparEnviosFisicosAr: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEnviosFisicosAr.pending, (state, action) => {
        const { processoArbitralId, page } = action.meta.arg;
        state.requestId = action.meta.requestId;
        // O erro NÃO é limpo aqui, só quando a resposta chega: é ele que mantém a aba
        // visível durante o "Tentar novamente" — sem carta e sem erro, a aba some e o
        // usuário é levado para a Visão geral no meio do retry.
        if (page === 1) {
          state.processoArbitralId = processoArbitralId;
          state.carregando = true;
        } else {
          state.carregandoMais = true;
        }
      })
      .addCase(fetchEnviosFisicosAr.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        const { page } = action.meta.arg;
        const novos = action.payload.items ?? [];
        // Na página seguinte, sem repetir o que já está na tela: uma carta nova no topo
        // empurra a lista e a borda da página anterior volta na próxima.
        const jaNaTela = new Set(state.itens.map((item) => item.id));
        state.itens =
          page === 1 ? novos : [...state.itens, ...novos.filter((n) => !jaNaTela.has(n.id))];
        state.total = action.payload.total;
        state.pagina = page;
        state.hasMore = action.payload.hasMore;
        state.carregando = false;
        state.carregandoMais = false;
        state.carregado = true;
        state.erro = null;
        state.paginaQueFalhou = null;
      })
      .addCase(fetchEnviosFisicosAr.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.carregando = false;
        state.carregandoMais = false;
        state.carregado = true;
        if (STATUS_SEM_ABA.has(action.payload?.status ?? null)) {
          state.itens = [];
          state.total = 0;
          state.hasMore = false;
          state.erro = null;
          state.paginaQueFalhou = null;
          return;
        }
        state.erro = action.payload?.mensagem ?? 'Não foi possível carregar o envio físico';
        state.paginaQueFalhou = action.meta.arg.page;
      })
      .addCase(abrirDocumentoEnvioFisicoAr.pending, (state, action) => {
        const { envioFisicoId, tipo } = action.meta.arg;
        state.documentosEmVoo[chaveDoDocumento(envioFisicoId, tipo)] = action.meta.requestId;
      })
      // A falha vira toast no hook, que tem o resultado do thunk; aqui só destrava o botão.
      .addCase(abrirDocumentoEnvioFisicoAr.fulfilled, (state, action) => {
        const { envioFisicoId, tipo } = action.meta.arg;
        destravar(state, chaveDoDocumento(envioFisicoId, tipo), action.meta.requestId);
      })
      .addCase(abrirDocumentoEnvioFisicoAr.rejected, (state, action) => {
        const { envioFisicoId, tipo } = action.meta.arg;
        destravar(state, chaveDoDocumento(envioFisicoId, tipo), action.meta.requestId);
      });
  },
});

export const { limparEnviosFisicosAr } = envioFisicoArSlice.actions;
export const envioFisicoArReducer = envioFisicoArSlice.reducer;
